import { useEffect, useState } from "react";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";

const today = () => new Date().toISOString().split("T")[0];

const getVal = (x, ...keys) => {
  for (const k of keys)
    if (x?.[k] !== undefined && x?.[k] !== null) return x[k];
  return "";
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const styles = {
  page: { fontSize: 11, padding: 0 },
  card: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: { fontSize: 13, fontWeight: 700 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: {
    height: 30,
    fontSize: 11,
    borderRadius: 7,
    fontWeight: 600,
    padding: "3px 10px",
  },
  tableWrap: {
    marginTop: 10,
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, marginBottom: 0, minWidth: 850 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    color: "#fff",
    whiteSpace: "nowrap",
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

export default function OpeningBalancePage() {
  const { user } = useAuth();
  const companyId = Number(
    user?.companyId || localStorage.getItem("companyId") || 0,
  );
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    entryDate: today(),
    accountId: "",
    type: "DEBIT",
    amount: "",
    notes: "",
  });

  useEffect(() => {
    loadAccounts();
    loadOpeningBalances();
  }, []);

  const loadAccounts = async () => {
    const res = await client.get("/accounts");
    setAccounts(Array.isArray(res.data) ? res.data : []);
  };

  const loadOpeningBalances = async () => {
    const res = await client.get("/finance/opening-balances");
    setRows(res.data || []);
  };

  const clearForm = () => {
    setForm({
      entryDate: today(),
      accountId: "",
      type: "DEBIT",
      amount: "",
      notes: "",
    });
  };

  const save = async () => {
    if (!form.accountId) return alert("Select account");
    if (!form.amount || Number(form.amount) <= 0)
      return alert("Enter valid amount");

    try {
      setSaving(true);

      if (!companyId)
        return alert(
          "Company is required. Please complete Company Setup or login again.",
        );

      await client.post("/finance/opening-balance", {
        companyId,
        entryDate: form.entryDate,
        accountId: Number(form.accountId),
        debit: form.type === "DEBIT" ? Number(form.amount) : 0,
        credit: form.type === "CREDIT" ? Number(form.amount) : 0,
        notes: form.notes,
      });

      alert("Opening balance saved");
      clearForm();
      loadOpeningBalances();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to save opening balance");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>
              <i className="bi bi-arrow-up-circle me-2"></i>
              Opening Balance
            </div>
            <div style={styles.sub}>
              Initial account balances with journal posting
            </div>
          </div>

          <span
            className="badge bg-light text-primary"
            style={{ fontSize: 10 }}
          >
            Journal Posting
          </span>
        </div>

        <div style={styles.body}>
          <div className="row g-2">
            <Field label="Date" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.entryDate}
                onChange={(e) =>
                  setForm({ ...form, entryDate: e.target.value })
                }
              />
            </Field>

            <Field label="Account" className="col-md-4">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.accountId}
                onChange={(e) =>
                  setForm({ ...form, accountId: e.target.value })
                }
              >
                <option value="">Select Account</option>
                {accounts.map((a, i) => {
                  const id = getVal(a, "AccountId", "accountId", "Id", "id");
                  const code = getVal(a, "AccountCode", "accountCode");
                  const name = getVal(
                    a,
                    "AccountName",
                    "accountName",
                    "Name",
                    "name",
                  );

                  return (
                    <option key={id || i} value={id}>
                      {code ? `${code} - ${name}` : name}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="Type" className="col-md-2">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="DEBIT">Debit</option>
                <option value="CREDIT">Credit</option>
              </select>
            </Field>

            <Field label="Amount" className="col-md-2">
              <input
                type="number"
                className="form-control form-control-sm text-end"
                style={styles.input}
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </Field>

            <div className="col-md-2 d-flex align-items-end gap-2">
              <button
                onClick={save}
                disabled={saving}
                className="btn btn-primary btn-sm"
                style={styles.btn}
              >
                <i className="bi bi-check2-circle me-1"></i>
                {saving ? "Saving..." : "Save"}
              </button>

              <button
                onClick={clearForm}
                className="btn btn-outline-secondary btn-sm"
                style={styles.btn}
              >
                Clear
              </button>
            </div>

            <Field label="Notes" className="col-md-12">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Example: Opening balance as per books on financial year start"
              />
            </Field>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Date",
                    "Voucher No",
                    "Account",
                    "Description",
                    "Debit",
                    "Credit",
                  ].map((h) => (
                    <th
                      key={h}
                      className={
                        ["Debit", "Credit"].includes(h) ? "text-end" : ""
                      }
                      style={styles.th}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      No opening balances
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => {
                    const date = getVal(r, "EntryDate", "entryDate");
                    const debit = num(getVal(r, "Debit", "debit"));
                    const credit = num(getVal(r, "Credit", "credit"));

                    return (
                      <tr key={i}>
                        <td style={styles.td}>
                          {date
                            ? new Date(date).toLocaleDateString("en-IN")
                            : ""}
                        </td>
                        <td style={{ ...styles.td, fontWeight: 700 }}>
                          {getVal(r, "VoucherNo", "voucherNo")}
                        </td>
                        <td style={styles.td}>
                          {getVal(r, "AccountName", "accountName")}
                        </td>
                        <td style={styles.td}>
                          {getVal(r, "Description", "description")}
                        </td>
                        <td className="text-end" style={styles.td}>
                          {debit > 0 ? debit.toFixed(2) : ""}
                        </td>
                        <td className="text-end" style={styles.td}>
                          {credit > 0 ? credit.toFixed(2) : ""}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}
